/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: SelectFaxDevice.tsx
 🗓️ Created: 2026-03-19
 🧩 Description:
    Searchable select component for choosing a Fax Device.
    Used in Extension form (Voicemail/Fax tab) and Fax Sending.
    - Supports id as string or number (generic)
    - Dropdown rendered via createPortal + position:fixed to avoid clipping
    - Displays type badge: "(Extension)", "(USER)", etc.
────────────────────────────────────────────────────────────────*/

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Printer } from 'lucide-react';

export interface FaxDeviceOption {
  id: string | number;
  name: string;
  number?: string;
  type?: string;
  enabled?: boolean;
}

interface SelectFaxDeviceProps {
  value: string | number | null;
  onChange: (value: string | number | null) => void;
  faxDevices?: FaxDeviceOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
}

/** Returns user-friendly label for a device entry */
function deviceLabel(d: FaxDeviceOption): string {
  const num = d.number?.trim();
  if (num) return `${d.name} (${num})`;
  if (d.type?.toUpperCase() === 'USER') return `${d.name} (Extension)`;
  if (d.type) return `${d.name} (${d.type})`;
  return d.name;
}

export default function SelectFaxDevice({
  value,
  onChange,
  faxDevices = [],
  placeholder = '— Select fax device —',
  disabled = false,
  className = '',
  error = false,
}: SelectFaxDeviceProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedDevice = value != null
    ? faxDevices.find((d) => String(d.id) === String(value)) ?? null
    : null;

  const filteredDevices = faxDevices.filter((d) => {
    const q = searchQuery.toLowerCase();
    return (
      d.name.toLowerCase().includes(q) ||
      (d.number ?? '').toLowerCase().includes(q) ||
      (d.type ?? '').toLowerCase().includes(q)
    );
  });

  const updateDropdownPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
    });
  }, []);

  const openDropdown = () => {
    if (disabled) return;
    updateDropdownPosition();
    setIsOpen(true);
  };

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        closeDropdown();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('scroll', closeDropdown, true);
    window.addEventListener('resize', closeDropdown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('scroll', closeDropdown, true);
      window.removeEventListener('resize', closeDropdown);
    };
  }, [isOpen, closeDropdown]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [isOpen]);

  const handleSelect = (id: string | number) => {
    onChange(id);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  const dropdown = isOpen ? (
    <div style={dropdownStyle} className="bg-popover border border-border rounded-md shadow-xl">
      {/* Search */}
      <div className="p-2 border-b border-border">
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search fax devices..."
          className="w-full px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      {/* List */}
      <div className="max-h-56 overflow-y-auto p-1">
        {filteredDevices.length > 0 ? (
          filteredDevices.map((d) => {
            const isSelected = String(d.id) === String(value);
            return (
              <button
                key={d.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); handleSelect(d.id); }}
                className={`
                  w-full px-3 py-2 text-left rounded-md
                  hover:bg-accent hover:text-accent-foreground
                  flex items-center justify-between gap-2
                  transition-colors
                  ${isSelected ? 'bg-accent text-accent-foreground' : ''}
                `}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Printer className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{d.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {d.number?.trim()
                        ? d.number
                        : d.type?.toUpperCase() === 'USER'
                          ? 'Extension'
                          : d.type ?? ''}
                      {d.type && <span className="ml-1 opacity-60">· {d.type}</span>}
                    </div>
                  </div>
                </div>
                {isSelected && <Check className="h-4 w-4 text-primary shrink-0" />}
              </button>
            );
          })
        ) : (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">
            {searchQuery ? 'No devices match your search' : 'No fax devices found'}
          </div>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className={`relative ${className}`}>
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        onClick={isOpen ? closeDropdown : openDropdown}
        disabled={disabled}
        className={`
          w-full px-3 py-2 text-left border rounded-md
          bg-background text-foreground
          ${error ? 'border-red-500' : 'border-input'}
          hover:bg-accent hover:text-accent-foreground
          focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
          disabled:opacity-50 disabled:cursor-not-allowed
          flex items-center justify-between gap-2
          transition-colors text-sm
        `}
      >
        <span className={`flex-1 truncate ${selectedDevice ? '' : 'text-muted-foreground'}`}>
          {selectedDevice ? deviceLabel(selectedDevice) : placeholder}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {selectedDevice && (
            <span
              onClick={handleClear}
              className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer px-1 text-base leading-none"
            >
              ×
            </span>
          )}
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Portal dropdown — avoids clipping by ancestor overflow */}
      {typeof document !== 'undefined' && createPortal(dropdown, document.body)}
    </div>
  );
}
